import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from '@/hooks/use-toast';
import { useUsers } from '@/hooks/use-users';
import {
  useAddProjectMember,
  useRemoveProjectMember,
} from '@/hooks/use-projects';
import {
  Calendar,
  Target,
  Users,
  CheckSquare,
  FileText,
  Loader2,
  UserPlus,
  X,
  ArrowLeft,
} from 'lucide-react';
import { cn, formatDate } from '@/lib/utils';

interface ProjectOverviewTabProps {
  project: any;
  isClient: boolean;
  derivedStages: { name: string; status: string }[];
}

export function ProjectOverviewTab({
  project,
  isClient,
  derivedStages,
}: ProjectOverviewTabProps) {
  const navigate = useNavigate();
  const [showAddMember, setShowAddMember] = useState(false);
  const [selectedNewMemberId, setSelectedNewMemberId] = useState('');
  const [removeMemberTarget, setRemoveMemberTarget] = useState<any>(null);

  const addMember = useAddProjectMember();
  const removeMember = useRemoveProjectMember();
  // User list is only needed for the staff-only "Add Member" dropdown —
  // skip the admin-only /users fetch entirely for clients.
  const { data: usersData } = useUsers({ enabled: !isClient });
  const allUsers = usersData?.data || [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Stages card */}
      <Card>
        <CardHeader>
          <CardTitle>Project Stages</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {derivedStages.map(({ name, status }) => (
              <div
                key={name}
                className="flex items-center justify-between p-3 rounded-lg bg-[var(--bg-secondary)]"
              >
                <div className="flex items-center gap-2">
                  <div
                    className={cn(
                      'w-2 h-2 rounded-full',
                      status === 'completed'
                        ? 'bg-dyndev-green'
                        : status === 'in_progress'
                          ? 'bg-blue-400'
                          : 'bg-gray-400'
                    )}
                  />
                  <span className="text-sm font-medium capitalize">
                    {name}
                  </span>
                </div>
                {name !== 'finalised' && (
                  <span
                    className={cn(
                      'text-xs px-2 py-0.5 rounded-full',
                      status === 'completed'
                        ? 'bg-dyndev-green/10 text-dyndev-green'
                        : status === 'in_progress'
                          ? 'bg-blue-500/10 text-blue-400'
                          : 'bg-gray-500/10 text-gray-400'
                    )}
                  >
                    {status.replace('_', ' ')}
                  </span>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Team Members card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Team Members</CardTitle>
            {!isClient && (
              <button
                onClick={() => setShowAddMember(!showAddMember)}
                className="flex items-center gap-1 text-xs text-dyndev-green hover:text-dyndev-green/80 transition-colors"
              >
                <UserPlus size={14} />
                {showAddMember ? 'Cancel' : 'Add Member'}
              </button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {/* Inline Add Member form */}
          {showAddMember && (
            <div className="mb-4 p-3 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-color)] space-y-2">
              <Select
                value={selectedNewMemberId}
                onValueChange={setSelectedNewMemberId}
              >
                <SelectTrigger aria-label="Select user to add">
                  <SelectValue placeholder="Select a user..." />
                </SelectTrigger>
                <SelectContent>
                  {allUsers
                    .filter(
                      (u: any) =>
                        !project.members?.some(
                          (m: any) => m.userId === u.id
                        )
                    )
                    .map((u: any) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.name} ({u.email}) — {u.role.replace('_', ' ')}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <Button
                size="sm"
                className="w-full"
                disabled={!selectedNewMemberId || addMember.isPending}
                onClick={async () => {
                  try {
                    await addMember.mutateAsync({
                      id: project.id,
                      userId: selectedNewMemberId,
                    });
                    toast({ title: 'Member added', variant: 'success' });
                    setSelectedNewMemberId('');
                  } catch (err: any) {
                    toast({
                      title: 'Failed to add member',
                      description:
                        err?.message || 'An unexpected error occurred.',
                      variant: 'destructive',
                    });
                  }
                }}
              >
                {addMember.isPending && (
                  <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
                )}
                Add to Project
              </Button>
            </div>
          )}

          {project.members?.length > 0 ? (
            <div className="space-y-3">
              {project.members.map((member: any) => (
                <div
                  key={member.id}
                  className="flex items-center gap-3 group"
                >
                  <div className="w-8 h-8 rounded-full bg-dyndev-green/10 text-dyndev-green flex items-center justify-center text-xs font-bold flex-shrink-0">
                    {member.user?.name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {member.user?.name}
                    </p>
                    <p className="text-xs text-[var(--text-secondary)] capitalize">
                      {member.role ||
                        member.user?.role?.replace('_', ' ')}
                    </p>
                  </div>
                  {!isClient && (
                    <button
                      onClick={() => setRemoveMemberTarget(member)}
                      className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg hover:bg-red-500/10 text-[var(--text-secondary)] hover:text-red-400 transition-all"
                      title="Remove member"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-[var(--text-secondary)]">
              No team members assigned yet.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Remove member confirmation */}
      <AlertDialog
        open={!!removeMemberTarget}
        onOpenChange={(open) => {
          if (!open) setRemoveMemberTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove team member</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove{' '}
              <strong>
                {removeMemberTarget?.user?.name || 'this user'}
              </strong>{' '}
              from this project? They will lose access to project
              resources and tasks.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (!removeMemberTarget) return;
                try {
                  await removeMember.mutateAsync({
                    id: project.id,
                    userId: removeMemberTarget.userId,
                  });
                  toast({
                    title: 'Member removed',
                    variant: 'success',
                  });
                  setRemoveMemberTarget(null);
                } catch (err: any) {
                  toast({
                    title: 'Failed to remove member',
                    description:
                      err?.message || 'An unexpected error occurred.',
                    variant: 'destructive',
                  });
                }
              }}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              {removeMember.isPending && (
                <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />
              )}
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
